import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Volume2, Wind, Wrench, Layers, MapPin, ArrowRight, Check, ChevronRight } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Contraintes acoustiques (sourced HomePage L266 + Realisations specs)
const specs = [
  { icon: Volume2, title: "Ventilation acoustique NC15-NC20", desc: "Installation de ventilation acoustique pour enregistrement professionnel." },
  { icon: Layers,  title: "Isolation boîte dans la boîte",    desc: "Type d'isolation acoustique déployé sur Studio Calogero pour garantir un silence absolu en enregistrement." },
  { icon: Wrench,  title: "Gainables et mini VRV",            desc: "Installation discrète via systèmes gainables avec régulation Airzone et mini VRV." },
  { icon: Wind,    title: "CTA double flux",                  desc: "Renouvellement d'air permanent sans compromis acoustique." }
];

// Réalisations référentes (texts from /realisations)
const projects = [
  {
    id: "calogero",
    title: "Studio Calogero",
    location: "Boulogne-Billancourt",
    specs: ["Isolation acoustique boîte dans la boîte", "Ventilation silencieuse NC15", "Double usage studio/résidentiel"]
  },
  {
    id: "after-now",
    title: "Studio After Now",
    location: "Toulouse",
    specs: ["3 gainables avec système Airzone", "Mini VRV haute performance", "CTA double flux", "Installation complète CVC"]
  }
];

const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function StudiosPage() {
  return (
    <>
      <Helmet>
        <title>Climatisation studio musique Paris | FLOW</title>
        <meta name="description" content="FLOW installe des systèmes de climatisation et ventilation silencieux NC15-NC20 pour studios d'enregistrement. Références : Studio Calogero (Boulogne-Billancourt), Studio After Now (Toulouse)." />
        <link rel="canonical" href="https://flow-air.com/secteurs/studios" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="studios-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Secteur — Studios</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Climatisation pour <span className="text-[#00AFFF]">studios d'enregistrement</span> et espaces audiovisuels
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW accompagne les studios d'enregistrement dans l'installation de systèmes de climatisation
              et ventilation silencieux. Nos installations prennent en compte les contraintes acoustiques
              et architecturales de chaque projet.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contraintes acoustiques */}
      <section className="py-16 md:py-24 bg-white" data-testid="studios-specs">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Savoir-faire</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Contraintes acoustiques studios
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {specs.map((spec, index) => {
              const Icon = spec.icon;
              return (
                <motion.div
                  key={spec.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`studios-spec-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{spec.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{spec.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Réalisations référentes */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]" data-testid="studios-projects">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Réalisations</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nos références studios
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {projects.map((project, index) => (
              <motion.article
                key={project.id}
                className="bg-white rounded-2xl p-8 shadow-sm border border-[#E2E8F0]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                data-testid={`studios-project-${index}`}
              >
                <div className="flex items-center gap-2 text-[#475569] text-sm mb-3">
                  <MapPin size={14} className="text-[#00AFFF]" />
                  <span>{project.location}</span>
                </div>
                <h3 className="text-xl font-bold text-[#0E1A2B] mb-5">{project.title}</h3>
                <ul className="space-y-2">
                  {project.specs.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-[#475569]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00AFFF] mt-2 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </motion.article>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/realisations"
              className="text-[#00AFFF] text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="studios-see-all"
            >
              Voir toutes nos réalisations <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Badges globaux */}
      <section className="py-12 bg-white">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F5F7FA] rounded-lg">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en climatisation et ventilation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="studios-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
